/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions -- Illustrative product scenes. */
import { useReducedMotion } from "framer-motion";
import { Check, FileText, MessageSquareText, Search, Sparkles } from "lucide-react";
import { type CSSProperties, Fragment, useEffect, useState } from "react";

import { careerStory } from "../home/sections/homepage/career-story";
import type { SceneId } from "./content";

/* Six looping mini-scenes, one per tool, that show the AI doing the work. Everything is
   illustrative: fictional people, no measured results. Motion is CSS keyframes keyed on mount,
   so a scene restarts each time it is selected. Base styles are the FINAL state and keyframes only
   define where an element starts, so with reduced motion each scene simply shows its outcome. */

const delay = (seconds: number) => ({ "--d": `${seconds}s` }) as CSSProperties;

const Wave = ({ bars = 22, className = "" }: { bars?: number; className?: string }) => (
  <span className={`sc-wave ${className}`} aria-hidden="true">
    {Array.from({ length: bars }, (_, i) => (
      <i
        key={i}
        style={
          {
            "--h": `${28 + ((i * 37) % 62)}%`,
            "--w": `${0.7 + ((i * 13) % 9) / 10}s`,
            "--d": `${(i % 7) * 0.09}s`,
          } as CSSProperties
        }
      />
    ))}
  </span>
);

type Token = { text: string; mark?: boolean };

/** Reveals words one by one, the way live captions and typed questions arrive. */
const TypedWords = ({
  tokens,
  start,
  step = 0.13,
  markDelay,
}: {
  tokens: Token[];
  start: number;
  step?: number;
  markDelay?: number;
}) => (
  <>
    {tokens.map((token, index) => (
      <Fragment key={index}>
        <span className="sc-word" style={delay(start + index * step)}>
          {token.mark ? <mark style={delay(markDelay ?? start)}>{token.text}</mark> : token.text}
        </span>{" "}
      </Fragment>
    ))}
  </>
);

const tokensOf = (text: string, marked: string[] = []): Token[] =>
  text.split(" ").map((word) => ({ text: word, mark: marked.includes(word) }));

/* 1. Resume builder: the AI matches the role's keywords and rewrites a weak bullet. */
export const ResumeScene = () => (
  <div className="sc sc-resume">
    <div className="sc-paper">
      <p className="sc-paper__name">{careerStory.name}</p>
      <p className="sc-paper__role">{careerStory.role}</p>
      <span className="sc-rule" />
      <p className="sc-eyebrow">Experience</p>
      <div className="sc-swap">
        <p className="sc-swap__old">Worked on the onboarding redesign with the team.</p>
        <p className="sc-swap__new">
          Led onboarding research that reshaped the first-day experience.
          <i className="sc-caret" aria-hidden="true" />
        </p>
      </div>
      <span className="sc-line" style={{ width: "82%" }} />
      <span className="sc-line" style={{ width: "64%" }} />
    </div>
    <div className="sc-jd">
      <p className="sc-eyebrow">
        <Search size={12} aria-hidden="true" /> The role asks for
      </p>
      <div className="sc-chips">
        <span className="sc-chip sc-chip--hit" style={delay(0.5)}>
          user research
        </span>
        <span className="sc-chip sc-chip--hit" style={delay(0.9)}>
          onboarding
        </span>
        <span className="sc-chip sc-chip--hit" style={delay(1.3)}>
          stakeholders
        </span>
        <span className="sc-chip">design systems</span>
      </div>
      <p className="sc-eyebrow sc-eyebrow--gap">Match to this role</p>
      <span className="sc-meter">
        <i style={delay(0.4)} />
      </span>
    </div>
  </div>
);

/* 2. Spotlight Intro: an AI prompt guides the recording and captions it live. */
const spotlightWords = careerStory.decision.split(" ");

export const SpotlightScene = () => (
  <div className="sc sc-spot">
    <figure className="sc-spot__frame">
      <img
        src={careerStory.portrait}
        alt=""
        width="400"
        height="500"
        loading="lazy"
        decoding="async"
      />
      <span className="sc-rec">
        <i aria-hidden="true" /> Recording
      </span>
      <figcaption className="sc-caption">
        {spotlightWords.map((word, index) => (
          <Fragment key={index}>
            <span className="sc-word" style={delay(0.7 + index * 0.24)}>
              {word}
            </span>{" "}
          </Fragment>
        ))}
      </figcaption>
    </figure>
    <div className="sc-spot__side">
      <div className="sc-prompt">
        <span className="sc-eyebrow">
          <Sparkles size={12} aria-hidden="true" /> AI prompt
        </span>
        <p>Tell them about a decision you made.</p>
      </div>
      <Wave bars={26} className="sc-wave--wide" />
      <p className="sc-saved" style={delay(3.2)}>
        <Check size={14} aria-hidden="true" /> Captions and transcript saved
      </p>
    </div>
  </div>
);

/* 3. Practice Interview: the AI listens, flags a vague answer and scores the delivery. */
const practiceAnswer = tokensOf(
  "We redesigned onboarding. I worked with the team, and we made the experience simpler.",
  ["worked", "with", "the", "team,"],
);
// Quote marks travel with the first and last word so they never wrap onto a line of their own.
practiceAnswer[0].text = `“${practiceAnswer[0].text}`;
practiceAnswer[practiceAnswer.length - 1].text += "”";

export const PracticeScene = () => (
  <div className="sc sc-practice">
    <div className="sc-q">
      <span className="sc-eyebrow">
        <MessageSquareText size={12} aria-hidden="true" /> AI interviewer
      </span>
      <p>{careerStory.question}</p>
    </div>
    <div className="sc-answer">
      <Wave bars={16} />
      <p>
        <TypedWords tokens={practiceAnswer} start={0.5} step={0.12} markDelay={2.2} />
      </p>
    </div>
    <p className="sc-note" style={delay(2.7)}>
      <Sparkles size={13} aria-hidden="true" />
      Make your own decision visible.
    </p>
    <dl className="sc-scores" style={delay(3.1)}>
      {[
        ["Relevance", 0.62],
        ["Pacing", 0.8],
        ["Filler words", 0.9],
      ].map(([label, value], index) => (
        <div key={label as string}>
          <dt>{label}</dt>
          <dd className="sc-meter">
            <i style={{ ...delay(3.1 + index * 0.18), transform: `scaleX(${value})` }} />
          </dd>
        </div>
      ))}
    </dl>
  </div>
);

/* 4. CV ranking: as the AI reads CVs against the criteria, the list re-orders. */
const criteria = ["User research", "Design systems", "Stakeholders"];
const people = [
  { id: "priya", name: "Priya N.", role: "Product designer", met: [true, true, true] },
  { id: "marcus", name: "Marcus L.", role: "UX researcher", met: [true, false, true] },
  { id: "jo", name: "Jo T.", role: "Interaction designer", met: [true, true, false] },
  { id: "sam", name: "Sam R.", role: "Visual designer", met: [false, true, false] },
];
const phases = [
  {
    order: ["marcus", "jo", "priya", "sam"],
    fit: { marcus: 0.6, jo: 0.52, priya: 0.46, sam: 0.3 },
  },
  {
    order: ["priya", "marcus", "jo", "sam"],
    fit: { priya: 0.92, marcus: 0.68, jo: 0.6, sam: 0.32 },
  },
] as const;
const ROW = 34;

export const RankingScene = () => {
  const reduced = useReducedMotion();
  const [phase, setPhase] = useState(reduced ? 1 : 0);
  useEffect(() => {
    if (reduced) {
      setPhase(1);
      return;
    }
    const timer = window.setTimeout(() => setPhase(1), 1900);
    return () => window.clearTimeout(timer);
  }, [reduced]);
  const { order, fit } = phases[phase];
  const read = phase === 1;

  return (
    <div className="sc sc-rank">
      <div className="sc-rank__head">
        <span className="sc-status" data-state={read ? "done" : "busy"}>
          <i aria-hidden="true" />
          {read ? "Ranked against your 3 criteria" : "Reading CVs against your criteria"}
        </span>
        <span className="sc-chips sc-chips--tight">
          {criteria.map((label) => (
            <span key={label} className="sc-chip sc-chip--tiny">
              {label}
            </span>
          ))}
        </span>
      </div>
      <ol className="sc-rank__list" style={{ height: people.length * ROW }}>
        {people.map((person) => {
          const rank = (order as readonly string[]).indexOf(person.id);
          return (
            <li
              key={person.id}
              className="sc-rank__row"
              data-top={read && rank === 0}
              style={{ transform: `translateY(${rank * ROW}px)` }}
            >
              <span className="sc-avatar" aria-hidden="true">
                {person.name[0]}
              </span>
              <span className="sc-rank__who">
                <strong>{person.name}</strong>
                <small>{person.role}</small>
              </span>
              <span className="sc-rank__met" aria-label="Criteria met">
                {person.met.map((met, index) => (
                  <i key={index} data-met={read && met} />
                ))}
              </span>
              <span className="sc-meter sc-meter--fit">
                <i
                  style={{
                    transform: `scaleX(${(fit as Record<string, number>)[person.id]})`,
                    transition: "transform 900ms cubic-bezier(0.22, 1, 0.36, 1)",
                  }}
                />
              </span>
            </li>
          );
        })}
      </ol>
      <p className="sc-decides">Your team decides who moves forward.</p>
    </div>
  );
};

/* 5. AI first-round interviews: the team picks who to invite; the AI runs the same interview. */
export const InvitesScene = () => (
  <div className="sc sc-invites">
    <p className="sc-eyebrow">Shortlist</p>
    <ul className="sc-invites__list">
      {[
        { name: "Priya N.", role: "Product designer", pick: 1.15 },
        { name: "Marcus L.", role: "UX researcher", pick: 0 },
        { name: "Jo T.", role: "Interaction designer", pick: 2.05 },
      ].map((person) => (
        <li key={person.name} data-picked={person.pick > 0}>
          <span className="sc-check" style={delay(person.pick)}>
            <Check size={12} aria-hidden="true" />
          </span>
          <span className="sc-rank__who">
            <strong>{person.name}</strong>
            <small>{person.role}</small>
          </span>
          {person.pick > 0 && (
            <span className="sc-sent" style={delay(3.45)}>
              Invited
            </span>
          )}
        </li>
      ))}
    </ul>
    <span className="sc-invite-btn">Invite 2 candidates</span>
    <p className="sc-toast" style={delay(3.35)}>
      <Sparkles size={13} aria-hidden="true" /> Same structured interview for everyone you invite.
    </p>
    <span className="sc-cursor" aria-hidden="true">
      <svg width="16" height="18" viewBox="0 0 16 18" fill="currentColor">
        <path d="M1 1l13 6.2-5.6 1.7L6.6 14.7z" />
      </svg>
      <em>You</em>
    </span>
  </div>
);

/* 6. Evidence review: every claim links to a moment in the interview, and you can ask. */
export const EvidenceScene = () => (
  <div className="sc sc-evidence">
    <div className="sc-evidence__top">
      <div className="sc-transcript">
        <p className="sc-eyebrow">
          <FileText size={12} aria-hidden="true" /> Transcript
        </p>
        <p>
          <time>02:09</time> I noticed people hesitated at the first decision.
        </p>
        <p>
          <time>02:14</time>{" "}
          <mark style={delay(1.1)}>I tested a prototype with new users first.</mark>
        </p>
      </div>
      <div className="sc-assess">
        <p className="sc-eyebrow">Assessment</p>
        <p style={delay(0.4)}>
          Owns their decisions <time>02:09</time>
        </p>
        <p style={delay(0.8)}>
          Tests ideas with users <time>02:14</time>
        </p>
      </div>
    </div>
    <div className="sc-ask">
      <p className="sc-ask__q">
        <Sparkles size={13} aria-hidden="true" />
        <span>
          <TypedWords tokens={tokensOf("Did they test their decision?")} start={1.6} step={0.14} />
        </span>
      </p>
      <p className="sc-ask__a" style={delay(3.1)}>
        Yes. At <time>02:14</time> they describe testing a prototype with new users first.
      </p>
    </div>
  </div>
);

export const SCENES: Record<SceneId, () => JSX.Element> = {
  resume: ResumeScene,
  spotlight: SpotlightScene,
  practice: PracticeScene,
  ranking: RankingScene,
  invites: InvitesScene,
  evidence: EvidenceScene,
};
